/* NVN Marine — Elasticsearch client
 *
 * Thin wrapper around https://search.nvnmarine.com/api/v1
 * Exposes: window.NVN.search.{search, autocomplete, suggest}, window.NVN.money
 *
 * Features:
 *   - AbortController per "slot" (search / autocomplete / suggest) so a new
 *     keystroke cancels the prior in-flight request
 *   - In-memory LRU cache (100 entries, 60s TTL) keyed by full URL
 *   - Errors return { ok: false, error } — callers fall back gracefully
 */
(function () {
  'use strict';

  var NVN = (window.NVN = window.NVN || {});
  var cfg = window.NVN_SEARCH || {};
  var base = (cfg.base || 'https://search.nvnmarine.com/api/v1').replace(/\/+$/, '');
  var apiKey = cfg.key || '';

  /* -- money formatting (USD, matches Liquid `| money` output) ------------ */
  var fmt;
  try {
    fmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
  } catch (_) {
    fmt = { format: function (n) { return '$' + (Number(n) || 0).toFixed(2); } };
  }
  NVN.money = function (dollars) {
    var n = Number(dollars);
    if (!isFinite(n)) n = 0;
    return fmt.format(n);
  };

  /* -- decode HTML entities from upstream strings -------------------------
   *
   * ES indexes product titles/descriptions in their HTML-encoded form
   * (e.g. `45"` is stored as `45&quot;`). Decode before passing into the
   * card renderer, which will re-escape via esc() to be safe in the DOM.
   * Order matters: decode `&amp;` last so already-correct ampersand-encoded
   * sequences aren't double-decoded.
   */
  NVN.decode = function (s) {
    if (s == null) return '';
    return String(s)
      .replace(/&quot;/g, '"')
      .replace(/&#34;/g,  '"')
      .replace(/&#039;/g, "'")
      .replace(/&#39;/g,  "'")
      .replace(/&apos;/g, "'")
      .replace(/&lt;/g,   '<')
      .replace(/&gt;/g,   '>')
      .replace(/&trade;/g, '™')
      .replace(/&reg;/g,   '®')
      .replace(/&copy;/g,  '©')
      .replace(/&nbsp;/g,  ' ')
      .replace(/&amp;/g,   '&');
  };

  /* -- LRU cache ---------------------------------------------------------- */
  var TTL_MS = 60 * 1000;
  var MAX_ENTRIES = 100;
  var cache = new Map(); // url -> { time, data }
  function cacheGet(url) {
    var hit = cache.get(url);
    if (!hit) return null;
    if (Date.now() - hit.time > TTL_MS) { cache.delete(url); return null; }
    // bump recency
    cache.delete(url); cache.set(url, hit);
    return hit.data;
  }
  function cacheSet(url, data) {
    if (cache.size >= MAX_ENTRIES) {
      var firstKey = cache.keys().next().value;
      cache.delete(firstKey);
    }
    cache.set(url, { time: Date.now(), data: data });
  }

  /* -- in-flight tracking ------------------------------------------------- */
  var inflight = new Map(); // slot -> AbortController
  function newSignal(slot) {
    var prev = inflight.get(slot);
    if (prev) try { prev.abort(); } catch (_) {}
    var ac = new AbortController();
    inflight.set(slot, ac);
    return ac;
  }
  function clearSignal(slot, ac) {
    if (inflight.get(slot) === ac) inflight.delete(slot);
  }

  /* -- URL builder -------------------------------------------------------- */
  function buildUrl(path, params) {
    var url = base + path;
    var pairs = [];
    if (params) {
      Object.keys(params).forEach(function (k) {
        var v = params[k];
        if (v === undefined || v === null || v === '') return;
        if (Array.isArray(v)) {
          if (!v.length) return;
          pairs.push(encodeURIComponent(k) + '=' + encodeURIComponent(v.join(',')));
        } else if (typeof v === 'boolean') {
          if (v) pairs.push(encodeURIComponent(k) + '=true');
        } else {
          pairs.push(encodeURIComponent(k) + '=' + encodeURIComponent(String(v)));
        }
      });
    }
    if (pairs.length) url += '?' + pairs.join('&');
    return url;
  }

  /* -- core GET ----------------------------------------------------------- */
  async function getJSON(url, slot) {
    var cached = cacheGet(url);
    if (cached) return cached;
    var ac = newSignal(slot);
    try {
      var res = await fetch(url, {
        method: 'GET',
        headers: { 'X-API-Key': apiKey, Accept: 'application/json' },
        signal: ac.signal,
        credentials: 'omit',
        mode: 'cors'
      });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      var data = await res.json();
      cacheSet(url, data);
      return data;
    } finally {
      clearSignal(slot, ac);
    }
  }

  /* -- public API --------------------------------------------------------- */
  var client = {
    /**
     * Search products.
     * @param {object} params - q, vendor[], product_type[], tags[], price_min,
     *                          price_max, in_stock, on_sale, sort, page, per_page
     */
    search: function (params) {
      var url = buildUrl('/search', params || {});
      return getJSON(url, 'search').then(
        function (json) { return { ok: true, json: json }; },
        function (err) {
          if (err && err.name === 'AbortError') return { ok: false, aborted: true };
          console.warn('[nvn-search] search failed', err);
          return { ok: false, error: err };
        }
      );
    },
    autocomplete: function (q) {
      var url = buildUrl('/autocomplete', { q: q });
      return getJSON(url, 'autocomplete').then(
        function (json) { return { ok: true, json: json }; },
        function (err) {
          if (err && err.name === 'AbortError') return { ok: false, aborted: true };
          return { ok: false, error: err };
        }
      );
    },
    suggest: function (q) {
      var url = buildUrl('/suggest', { q: q });
      return getJSON(url, 'suggest').then(
        function (json) { return { ok: true, json: json }; },
        function (err) {
          if (err && err.name === 'AbortError') return { ok: false, aborted: true };
          return { ok: false, error: err };
        }
      );
    }
  };

  NVN.search = client;
  NVN.config = {
    base: base,
    perPage: parseInt(cfg.perPage, 10) || 24,
    autocompleteCount: parseInt(cfg.autocompleteCount, 10) || 8,
    showOutOfStock: cfg.showOutOfStock !== false
  };
})();
